import type { Dispatch, SetStateAction } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { api } from '@koridor/api-client';
import type { GameConfig } from '../contexts/GameContext';
import { buildGameLaunch, launchGame } from './launch-game';

export type InviteAcceptedPayload = {
  matchId?: string;
  boardSize?: number;
  ranked?: boolean;
  opponent?: string;
  opponentId?: string;
};

let enteredInviteMatchId: string | null = null;
let wired = false;

type InviteHandler = (payload: InviteAcceptedPayload) => void;
const inviteHandlers = new Set<InviteHandler>();

export function resetInviteEntryLock() {
  enteredInviteMatchId = null;
}

export function enterInviteOnce(
  payload: InviteAcceptedPayload,
  navigate: NavigateFunction,
  setGameCfg: Dispatch<SetStateAction<GameConfig>>,
): boolean {
  const matchId = payload?.matchId;
  if (!matchId) return false;
  if (enteredInviteMatchId === matchId) return false;
  enteredInviteMatchId = matchId;
  launchGame(buildGameLaunch({
    size: (payload.boardSize ?? 9) as 7 | 9,
    ranked: payload.ranked ?? false,
    local: false,
    mode: 'friend',
    matchId,
    opponent: payload.opponent,
    opponentId: payload.opponentId,
  }), navigate, setGameCfg);
  return true;
}

export function onInviteAccepted(handler: InviteHandler) {
  inviteHandlers.add(handler);
  return () => { inviteHandlers.delete(handler); };
}

/** Single WS fan-out for invite.accepted — avoids duplicate launchGame. */
export function ensureInviteAcceptedSignal() {
  if (wired) return () => {};
  wired = true;
  const off = api.on('invite.accepted', (payload: unknown) => {
    const p = payload as InviteAcceptedPayload;
    if (!p?.matchId) return;
    for (const handler of inviteHandlers) handler(p);
  });
  return () => {
    off();
    wired = false;
  };
}
